import React, { useState } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SubscriptionManager from './subscription/SubscriptionManager';
import { apiPost } from '../utils/apiUtils';
import { useAuth } from '../context/AuthContext';

/**
 * OnboardingFlow Component
 * Displays pricing options for new users during onboarding
 * Allows selecting Free tier or paid subscription
 */
const OnboardingFlow = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isStartingFree, setIsStartingFree] = useState(false);
  const [isInCheckout, setIsInCheckout] = useState(false);

  const handleStartFree = async () => {
    try {
      setIsStartingFree(true);

      // Update user tier to 'free'
      const response = await apiPost('/api/profile/tier', { tier: 'free' });

      if (!response.ok) {
        console.error('Failed to set free tier');
        // Continue anyway - user can always upgrade later
      }

      // Redirect to main app
      navigate('/');
    } catch (error) {
      console.error('Error starting free:', error);
      // Continue anyway
      navigate('/');
    }
  };

  const handleSkip = () => {
    // Skip plan selection, go to app with default tier
    navigate('/');
  };

  const handleCheckoutChange = (isCheckingOut) => {
    setIsInCheckout(isCheckingOut);
  };

  const handleSubscriptionSuccess = () => {
    // Redirect to main app after successful subscription
    navigate('/');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#0D0D21',
      py: 4
    }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4, px: 2 }}>
        <Typography variant="h3" sx={{ color: '#0095FF', fontWeight: 700, mb: 2 }}>
          Choose Your Plan
        </Typography>
        <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 3 }}>
          Select the plan that's right for you. You can always upgrade later.
        </Typography>
      </Box>

      {/* Subscription Manager - Shows pricing cards */}
      <Box sx={{ px: 2 }}>
        <SubscriptionManager
          isOnboarding={true}
          onCheckoutChange={handleCheckoutChange}
          onSubscriptionSuccess={handleSubscriptionSuccess}
        />
      </Box>

      {/* Free Tier Option at Bottom - Hide when in checkout */}
      {!isInCheckout && (
        <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
          <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 3 }}>
            or
          </Typography>
          <Paper sx={{
            maxWidth: 600,
            mx: 'auto',
            p: 3,
            bgcolor: 'rgba(0, 149, 255, 0.1)',
            border: '2px solid #0095FF'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#FFFFFF' }}>
              Start for Free
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: '#FFFFFF' }}>
              100 credits per month - Perfect to try Wyspur AI
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleStartFree}
              disabled={isStartingFree}
              sx={{
                bgcolor: '#0095FF',
                '&:hover': { bgcolor: '#0077CC' },
                px: 4,
                py: 1.5
              }}
            >
              {isStartingFree ? 'Setting up...' : 'START FREE'}
            </Button>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default OnboardingFlow;
